import { useEffect, useMemo, useState } from 'react';
import { Badge, Breadcrumb, Button, DatePicker, Empty, Form, Input, List, Modal, Space, Table, Tag, message } from 'antd';
import { CheckCircleOutlined, DeleteOutlined, EditOutlined, HomeOutlined, PlusOutlined, PrinterOutlined, SaveOutlined, StopOutlined, TeamOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';

const genderDormitoryType = { 男: '男生寝室', 女: '女生寝室' };

const DormitoryInspectionPage = () => {
  const [tasks, setTasks] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [dormitories, setDormitories] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [taskOpen, setTaskOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const [selectedDormitoryIds, setSelectedDormitoryIds] = useState([]);
  const [buildingFilter, setBuildingFilter] = useState('全部楼栋');
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    try {
      const [taskRes, teacherRes, dormRes] = await Promise.all([
        api.get('/dormitory-inspection/tasks'),
        api.get('/dormitory-inspection/teachers'),
        api.get('/accommodation/dormitories'),
      ]);
      setTasks(taskRes.data || []);
      setTeachers(teacherRes.data || []);
      setDormitories(dormRes.data || []);
    } catch {
      message.error('获取查寝任务数据失败');
    }
  };

  useEffect(() => { load(); }, []);

  // Old task assignments can remain after a dormitory is removed. Only count rooms that still exist.
  const validDormitoryIds = useMemo(
    () => new Set(dormitories.map(item => item.id)),
    [dormitories]
  );
  const validAssignments = useMemo(
    () => assignments.filter(item => validDormitoryIds.has(item.dormitoryId)),
    [assignments, validDormitoryIds]
  );
  const ownerByDormitory = useMemo(() => {
    const owners = new Map();
    validAssignments.forEach(item => owners.set(item.dormitoryId, item.teacherId));
    return owners;
  }, [validAssignments]);

  const selectedTeacher = teachers.find(teacher => teacher.id === selectedTeacherId);
  const roomType = selectedTeacher ? genderDormitoryType[selectedTeacher.gender] : null;
  const buildings = useMemo(() => [...new Set(dormitories.map(item => item.building).filter(Boolean))].sort(), [dormitories]);
  const visibleDormitories = useMemo(() => dormitories
    .filter(item => item.genderType === roomType)
    .filter(item => buildingFilter === '全部楼栋' || item.building === buildingFilter)
    .sort((a, b) => String(a.building + '-' + a.dormitoryNumber).localeCompare(String(b.building + '-' + b.dormitoryNumber), 'zh-CN', { numeric: true })), [dormitories, roomType, buildingFilter]);
  const assignmentCount = teacherId => validAssignments.filter(item => item.teacherId === teacherId).length;

  const chooseTeacher = teacher => {
    setSelectedTeacherId(teacher.id);
    setSelectedDormitoryIds(validAssignments.filter(item => item.teacherId === teacher.id).map(item => item.dormitoryId));
  };

  const openCreateTask = () => {
    setEditingTask(null);
    form.resetFields();
    setTaskOpen(true);
  };

  const openEditTask = task => {
    setEditingTask(task);
    form.setFieldsValue({ name: task.name, period: [dayjs(task.startDate), dayjs(task.endDate)] });
    setTaskOpen(true);
  };

  const saveTask = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name,
        startDate: values.period[0].format('YYYY-MM-DD'),
        endDate: values.period[1].format('YYYY-MM-DD'),
      };
      if (editingTask) {
        await api.put('/dormitory-inspection/tasks/' + editingTask.id, payload);
      } else {
        await api.post('/dormitory-inspection/tasks', payload);
      }
      message.success(editingTask ? '查寝任务已更新' : '查寝任务已创建');
      setTaskOpen(false);
      setEditingTask(null);
      form.resetFields();
      load();
    } catch (error) {
      if (!error.errorFields) message.error(error.response?.data?.message || (editingTask ? '更新查寝任务失败' : '创建查寝任务失败'));
    }
  };

  const stopTask = async task => {
    try {
      await api.post('/dormitory-inspection/tasks/' + task.id + '/stop');
      if (currentTask?.id === task.id) { setAssignmentOpen(false); setCurrentTask(null); }
      message.success('查寝任务已停止，生活老师将不再收到该任务');
      load();
    } catch (error) {
      message.error(error.response?.data?.message || '停止查寝任务失败');
    }
  };

  const deleteTask = async task => {
    try {
      await api.delete('/dormitory-inspection/tasks/' + task.id);
      if (currentTask?.id === task.id) { setAssignmentOpen(false); setCurrentTask(null); }
      message.success('查寝任务已删除');
      load();
    } catch (error) {
      message.error(error.response?.data?.message || '删除查寝任务失败');
    }
  };

  const openAssignment = async task => {
    setCurrentTask(task);
    setBuildingFilter('全部楼栋');
    try {
      const response = await api.get('/dormitory-inspection/tasks/' + task.id + '/assignments');
      const loaded = response.data || [];
      setAssignments(loaded);
      const firstTeacher = teachers.find(teacher => genderDormitoryType[teacher.gender]) || teachers[0] || null;
      setSelectedTeacherId(firstTeacher?.id || null);
      setSelectedDormitoryIds(firstTeacher ? loaded.filter(item => item.teacherId === firstTeacher.id && validDormitoryIds.has(item.dormitoryId)).map(item => item.dormitoryId) : []);
      setAssignmentOpen(true);
    } catch {
      message.error('获取任务分配情况失败');
    }
  };

  const toggleDormitory = dormitory => {
    const ownerId = ownerByDormitory.get(dormitory.id);
    if (!selectedTeacher || (ownerId && ownerId !== selectedTeacher.id)) return;
    setSelectedDormitoryIds(current => current.includes(dormitory.id)
      ? current.filter(id => id !== dormitory.id)
      : [...current, dormitory.id]);
  };

  const saveCurrentTeacher = async () => {
    if (!selectedTeacher) return message.warning('请先选择后勤生活老师');
    if (!genderDormitoryType[selectedTeacher.gender]) return message.warning('该生活老师尚未设置性别，不能分配寝室');
    setSaving(true);
    try {
      await api.post('/dormitory-inspection/tasks/' + currentTask.id + '/assignments', {
        teacherId: selectedTeacher.id,
        dormitoryIds: selectedDormitoryIds,
      });
      const response = await api.get('/dormitory-inspection/tasks/' + currentTask.id + '/assignments');
      setAssignments(response.data || []);
      message.success(selectedTeacher.name + '的寝室分配已保存');
    } catch (error) {
      message.error(error.response?.data?.reason || error.response?.data?.message || '保存分配失败');
    } finally {
      setSaving(false);
    }
  };

  const escapePrintHtml = value => String(value ?? '').replace(/[&<>"']/g, character => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[character]));

  const compareDormitory = (left, right) => {
    const building = String(left.building || '').localeCompare(String(right.building || ''), 'zh-CN', { numeric: true });
    if (building !== 0) return building;
    return String(left.dormitoryNumber || '').localeCompare(String(right.dormitoryNumber || ''), 'zh-CN', { numeric: true });
  };

  const printInspectionSheet = async task => {
    try {
      const [assignmentResponse, studentResponse] = await Promise.all([
        api.get('/dormitory-inspection/tasks/' + task.id + '/assignments'),
        api.get('/accommodation/students'),
      ]);
      const taskAssignments = assignmentResponse.data || [];
      const residentStudents = studentResponse.data || [];
      if (!taskAssignments.length) {
        message.warning('该查寝任务尚未分配寝室，无法打印');
        return;
      }

      const dormitoryById = new Map(dormitories.map(item => [item.id, item]));
      const teacherById = new Map(teachers.map(item => [item.id, item]));
      const groupedAssignments = taskAssignments.reduce((result, assignment) => {
        if (!result.has(assignment.teacherId)) result.set(assignment.teacherId, []);
        result.get(assignment.teacherId).push(assignment);
        return result;
      }, new Map());
      const pages = [...groupedAssignments.entries()].map(([teacherId, teacherAssignments]) => {
        const teacher = teacherById.get(teacherId);
        const assignedDormitories = teacherAssignments
          .map(assignment => dormitoryById.get(assignment.dormitoryId))
          .filter(Boolean)
          .sort(compareDormitory);
        const assignedKeys = new Set(assignedDormitories.map(dormitory => String(dormitory.building || '') + '|' + String(dormitory.dormitoryNumber || '')));
        const studentsByDormitory = residentStudents.reduce((result, student) => {
          const key = String(student.building || '') + '|' + String(student.dormitoryNumber || '');
          if (!assignedKeys.has(key)) return result;
          if (!result.has(key)) result.set(key, []);
          result.get(key).push(student);
          return result;
        }, new Map());
        const rows = assignedDormitories.flatMap(dormitory => {
          const key = String(dormitory.building || '') + '|' + String(dormitory.dormitoryNumber || '');
          const students = (studentsByDormitory.get(key) || []).sort((left, right) => String(left.bedNumber || '').localeCompare(String(right.bedNumber || ''), 'zh-CN', { numeric: true }));
          return students.length ? students.map(student => ({ dormitory, student })) : [{ dormitory, student: null }];
        });
        return { teacher, rows };
      }).filter(page => page.rows.length);

      if (!pages.length) {
        message.warning('任务分配的寝室数据不存在，无法打印');
        return;
      }

      const printWindow = window.open('', '_blank', 'width=1200,height=800');
      if (!printWindow) {
        message.error('浏览器拦截了打印窗口，请允许弹出窗口后重试');
        return;
      }
      const printedOn = dayjs().format('YYYY年MM月DD日');
      const pageHtml = pages.map(({ teacher, rows }) => {
        const rowHtml = rows.map(({ dormitory, student }) => {
          const values = student ? [
            dormitory.building,
            dormitory.dormitoryNumber,
            student.bedNumber,
            student.name,
            student.gender,
            student.school,
            student.major,
            student.phone,
            student.className,
            '',
          ] : [dormitory.building, dormitory.dormitoryNumber, '', '', '', '', '', '', '', '空寝室'];
          return '<tr>' + values.map(value => '<td>' + escapePrintHtml(value) + '</td>').join('') + '</tr>';
        }).join('');
        return '<section class="print-page">' +
          '<h1>查寝单</h1>' +
          '<div class="meta"><span>查寝任务：' + escapePrintHtml(task.name) + '</span><span>查寝周期：' + escapePrintHtml(task.startDate) + ' 至 ' + escapePrintHtml(task.endDate) + '</span><span>后勤生活老师：' + escapePrintHtml(teacher?.name || '未找到老师') + '</span><span>打印日期：' + printedOn + '</span></div>' +
          '<table><thead><tr><th>楼栋</th><th>寝室</th><th>床位</th><th>姓名</th><th>性别</th><th>学校</th><th>升本专业</th><th>本人联系方式</th><th>班级</th><th>备注</th></tr></thead><tbody>' + rowHtml + '</tbody></table>' +
          '<div class="signature"><span>查寝老师签字：________________</span><span>查寝日期：________________</span></div>' +
          '</section>';
      }).join('');
      printWindow.document.write('<!doctype html><html lang="zh-CN"><head><meta charset="utf-8"><title>查寝单-' + escapePrintHtml(task.name) + '</title><style>@page { size: A4 landscape; margin: 10mm; } * { box-sizing: border-box; } body { margin: 0; color: #000; font-family: "Microsoft YaHei", SimSun, sans-serif; } .print-page { min-height: 185mm; page-break-after: always; break-after: page; } .print-page:last-child { page-break-after: auto; break-after: auto; } h1 { margin: 0 0 12px; text-align: center; font-size: 21px; font-weight: 700; } .meta { display: grid; grid-template-columns: 1fr 1.4fr 1fr 1fr; gap: 8px 14px; margin-bottom: 10px; font-size: 12px; } table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 11px; } th, td { border: 1px solid #000; padding: 5px 4px; text-align: center; vertical-align: middle; word-break: break-all; height: 30px; } th { background: #f2f2f2; font-weight: 700; } th:nth-child(1), th:nth-child(2), th:nth-child(3), th:nth-child(5) { width: 7%; } th:nth-child(4) { width: 8%; } th:nth-child(6), th:nth-child(7), th:nth-child(9) { width: 12%; } th:nth-child(8) { width: 15%; } th:nth-child(10) { width: 11%; } .signature { display: flex; justify-content: flex-end; gap: 42px; margin-top: 14px; font-size: 12px; } @media print { .print-page { page-break-after: always; } .print-page:last-child { page-break-after: auto; } }</style></head><body>' + pageHtml + '<script>window.onload = function () { window.focus(); window.print(); };</script></body></html>');
      printWindow.document.close();
    } catch (error) {
      message.error(error.response?.data?.message || '生成查寝单失败');
    }
  };

  const columns = [
    { title: '任务名称', dataIndex: 'name' },
    { title: '查寝周期', render: (_, task) => task.startDate + ' 至 ' + task.endDate },
    { title: '频次', render: () => <Tag color="blue">每天</Tag> },
    { title: '状态', dataIndex: 'status', render: value => {
      const stopped = value === '已停止';
      return <Badge status={stopped ? 'default' : 'processing'} text={value || '进行中'} />;
    } },
    { title: '操作', width: 450, render: (_, task) => {
      const stopped = task.status === '已停止';
      return <Space size="small">
        <Button type="link" icon={<EditOutlined />} onClick={() => openEditTask(task)}>编辑</Button>
        {!stopped && <Button type="link" danger icon={<StopOutlined />} onClick={() => Modal.confirm({ title: '停止查寝任务', content: '停止后，后勤生活老师将不再收到该任务；已完成的查寝记录会保留。', okText: '确认停止', okButtonProps: { danger: true }, cancelText: '取消', onOk: () => stopTask(task) })}>停止</Button>}
        {!stopped && <Button type="link" icon={<TeamOutlined />} onClick={() => openAssignment(task)}>任务分配</Button>}
        <Button type="link" icon={<PrinterOutlined />} onClick={() => printInspectionSheet(task)}>打印查寝单</Button>
        <Button type="link" danger icon={<DeleteOutlined />} onClick={() => Modal.confirm({ title: '删除查寝任务', content: '删除后将同时清除该任务的寝室分配和查寝记录，且无法恢复。', okText: '确认删除', okButtonProps: { danger: true }, cancelText: '取消', onOk: () => deleteTask(task) })}>删除</Button>
      </Space>;
    } },
  ];

  return <div>
    <div className="page-header">
      <Breadcrumb items={[{ title: <><HomeOutlined /> 首页</> }, { title: '查寝任务' }]} />
      <h2 className="page-title">查寝任务管理</h2>
    </div>
    <div className="table-card">
      <Button type="primary" icon={<PlusOutlined />} onClick={openCreateTask} style={{ marginBottom: 20 }}>新建查寝任务</Button>
      <Table rowKey="id" columns={columns} dataSource={tasks} pagination={{ pageSize: 10 }} />
    </div>

    <Modal title={editingTask ? '编辑查寝任务' : '新建周期性查寝任务'} open={taskOpen} onCancel={() => { setTaskOpen(false); setEditingTask(null); form.resetFields(); }} onOk={saveTask} okText={editingTask ? '保存修改' : '创建'}>
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="任务名称" rules={[{ required: true, message: '请输入任务名称' }]}><Input placeholder="例如：暑假每日查寝" /></Form.Item>
        <Form.Item name="period" label="查寝周期" rules={[{ required: true, message: '请选择开始和结束日期' }]}><DatePicker.RangePicker style={{ width: '100%' }} disabledDate={date => date && date < dayjs().startOf('day')} /></Form.Item>
      </Form>
    </Modal>

    <Modal title="查寝任务分配" open={assignmentOpen} onCancel={() => setAssignmentOpen(false)} footer={null} width={1120} destroyOnClose>
      <div style={{ display: 'flex', gap: 32, marginBottom: 20, padding: '14px 16px', background: '#f6f8fb', border: '1px solid #e6edf5' }}>
        <div><div style={{ color: '#6b7280', fontSize: 12 }}>当前任务</div><strong>{currentTask?.name}</strong></div>
        <div><div style={{ color: '#6b7280', fontSize: 12 }}>查寝周期</div><strong>{currentTask?.startDate} 至 {currentTask?.endDate}</strong></div>
        <div><div style={{ color: '#6b7280', fontSize: 12 }}>查寝频次</div><Tag color="blue" style={{ marginInlineEnd: 0 }}>每天</Tag></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '270px minmax(0, 1fr)', minHeight: 480, border: '1px solid #e5e7eb' }}>
        <section style={{ borderRight: '1px solid #e5e7eb', background: '#fafafa' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>后勤生活老师</div>
          <List dataSource={teachers} locale={{ emptyText: '暂无后勤生活老师' }} renderItem={teacher => {
            const active = selectedTeacherId === teacher.id;
            const eligible = Boolean(genderDormitoryType[teacher.gender]);
            return <List.Item onClick={() => chooseTeacher(teacher)} style={{ cursor: 'pointer', padding: '12px 14px', background: active ? '#e6f4ff' : 'transparent', borderInlineStart: active ? '3px solid #1677ff' : '3px solid transparent' }}>
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><strong>{teacher.name}</strong><Tag color={teacher.gender === '男' ? 'blue' : teacher.gender === '女' ? 'magenta' : 'default'} style={{ margin: 0 }}>{teacher.gender || '未设置'}</Tag></div>
                <div style={{ marginTop: 5, color: eligible ? '#6b7280' : '#d46b08', fontSize: 12 }}>{eligible ? '已负责 ' + assignmentCount(teacher.id) + ' 间寝室' : '需先在老师资料中设置性别'}</div>
              </div>
            </List.Item>;
          }} />
        </section>
        <section style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <div><strong>{selectedTeacher ? selectedTeacher.name + '负责的寝室' : '请选择生活老师'}</strong>{selectedTeacher && <span style={{ marginLeft: 10, color: '#6b7280', fontSize: 12 }}>{roomType || '性别未设置'}</span>}</div>
            <Space wrap><span style={{ color: '#6b7280', fontSize: 12 }}>楼栋筛选</span>{['全部楼栋', ...buildings].map(building => <Button key={building} size="small" type={buildingFilter === building ? 'primary' : 'default'} onClick={() => setBuildingFilter(building)}>{building}</Button>)}</Space>
          </div>
          <div style={{ padding: 16, overflowY: 'auto', maxHeight: 475 }}>
            {!selectedTeacher ? <Empty description="请选择左侧生活老师后分配寝室" /> : !roomType ? <Empty description="该生活老师未设置性别，暂不能分配寝室" /> : visibleDormitories.length === 0 ? <Empty description="没有可分配的对应性别寝室" /> : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(135px, 1fr))', gap: 12 }}>{visibleDormitories.map(dormitory => {
              const ownerId = ownerByDormitory.get(dormitory.id);
              const owner = teachers.find(teacher => teacher.id === ownerId);
              const ownedByOther = ownerId && ownerId !== selectedTeacher.id;
              const selected = selectedDormitoryIds.includes(dormitory.id);
              const occupiedBeds = Array.isArray(dormitory.residents)
                ? dormitory.residents.length
                : Number(dormitory.occupiedBeds) || 0;
              return <button type="button" key={dormitory.id} onClick={() => toggleDormitory(dormitory)} disabled={ownedByOther} style={{ minHeight: 116, padding: 12, textAlign: 'left', cursor: ownedByOther ? 'not-allowed' : 'pointer', border: '1px solid ' + (selected ? '#1677ff' : ownedByOther ? '#e5e7eb' : '#cbd5e1'), background: selected ? '#e6f4ff' : ownedByOther ? '#f5f5f5' : '#fff', color: ownedByOther ? '#8c8c8c' : '#1f2937', borderRadius: 6 }}>
                <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 6 }}><strong style={{ fontSize: 16 }}>{dormitory.building}-{dormitory.dormitoryNumber}</strong>{selected && <CheckCircleOutlined style={{ color: '#1677ff', fontSize: 17 }} />}</div>
                <div style={{ marginTop: 8, fontSize: 12 }}>{dormitory.genderType}</div><div style={{ marginTop: 4, fontSize: 12 }}>入住 {occupiedBeds}/{dormitory.bedCapacity || 0} 人</div>
                <div style={{ marginTop: 9, fontSize: 12, color: ownedByOther ? '#d46b08' : selected ? '#1677ff' : '#6b7280' }}>{ownedByOther ? '已分配：' + (owner?.name || '其他老师') : selected ? '已选择' : '点击分配'}</div>
              </button>;
            })}</div>}
          </div>
        </section>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
        <span style={{ color: '#6b7280' }}>{selectedTeacher ? selectedTeacher.name + '当前选择 ' + selectedDormitoryIds.length + ' 间寝室' : ''}</span>
        <Space><Button onClick={() => setAssignmentOpen(false)}>关闭</Button><Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={!selectedTeacher || !roomType} onClick={saveCurrentTeacher}>保存当前老师分配</Button></Space>
      </div>
    </Modal>
  </div>;
};

export default DormitoryInspectionPage;


